import React, { useState } from 'react';
import { alpha, Box, Stack, Typography } from '@mui/material';
import { CameraAlt as CameraIcon } from '@mui/icons-material';
import { tokens } from '../../../../theme/tokens';
import { StagedSourceBadge } from '../../../StagedSourceBadge';
import { BrandSelect } from '../../../ui';
import { BarcodeTextField } from '../../BarcodeTextField';
import { fieldProvenanceChip, formatLaunchDateValue, inferLaunchPrecision, type LaunchDatePrecision } from '../../launchDateDisplay';
import { ProductLifecycleEditor, type ProductLifecycleInput } from '../../ProductLifecycleEditor';
import { PRODUCT_CATEGORY_SELECT_GROUPS, PRODUCT_CATEGORY_SLUGS_LIST } from '../../../../constants/productCategories';
import { BannerAction, ChipList, DetailCard, Dot, Field, FieldGrid, KeyValueRows, LinkButton, Pill, SelectInput, StatStrip, TextInput, WarningBanner } from '../primitives';
import type { ProductDraft } from '../productDraft';
import { toDate } from '../productDraft';
import type { AdminCanonicalProduct, AdminProductVariant, ClassificationIssue, PriceCheckIssue } from '../productDetailTypes';
import { fieldSource, formatDate, formatDateTime, formatShortDate, money } from '../productDetailFormat';

/** `ProductType` / `ProductTypeExpanded` in src/types/canonicalProduct.ts. */
const PRODUCT_TYPES = ['camera', 'lens', 'accessory'];
const PRODUCT_TYPES_EXPANDED = ['camera', 'lens', 'lens-accessory', 'flash', 'tripod', 'bag', 'battery', 'memory', 'filter', 'strap', 'grip', 'remote', 'monitor', 'audio', 'cable', 'drone', 'software', 'cleaning', 'accessory'];
/** Mirrors STATUS_OPTIONS on the page: `placeholder` is never offered as a pick. */
const STATUS_OPTIONS = ['active', 'draft', 'archived', 'discontinued'];

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

export const statusTone = (status?: string) =>
  status === 'active' ? ('positive' as const) : status === 'discontinued' || status === 'draft' ? ('caution' as const) : ('neutral' as const);

interface Props {
  product: AdminCanonicalProduct;
  draft: ProductDraft;
  base: ProductDraft;
  editing: boolean;
  update: (patch: Partial<ProductDraft>) => void;
  repairCard: React.ReactNode;
  classification: ClassificationIssue | null;
  priceCheck: PriceCheckIssue | null;
  applyFix: (payload: Record<string, unknown>, message: string) => Promise<void>;
  fixing: boolean;
  goTo: (tab: 'media' | 'source' | 'pricing') => void;
  /** Saves the pre-release lifecycle at once, and the panel Save includes a pending edit. */
  saveLifecycle: (input: ProductLifecycleInput | null) => Promise<void>;
  onLifecyclePending?: (pending: ProductLifecycleInput | null | undefined) => void;
  onLifecycleInvalid?: (invalid: boolean) => void;
  /** Opens a sibling variant in the same dialog. */
  onOpenProduct?: (next: { id: string; sku?: string; name?: string }) => void;
  /**
   * A store or vendor editing its own submitted product (MOT-695): only the
   * content it may change is shown as editable, and Motzu's own record,
   * pricing, status and lifecycle controls are left out.
   */
  submitter?: boolean;
  /**
   * A submitter creating the product (MOT-701): brand and its own SKU are
   * editable (they are fixed only once submitted), and the MSRP and MAP it
   * knows are entered here, with `priceNote` saying where they go.
   */
  creating?: { priceNote: string; skuHint: string };
}

export const OverviewTab: React.FC<Props> = ({ product, draft, base, editing, update, repairCard, classification, priceCheck, applyFix, fixing, goTo, saveLifecycle, onLifecyclePending, onLifecycleInvalid, onOpenProduct, submitter = false, creating }) => {
  const [selected, setSelected] = useState(0);
  const images = draft.images;
  const hero = images[selected] || images.find((i) => i.isPrimary) || images[0];
  const thumbs = images.slice(0, 6);
  const extra = images.length - 5;
  const currency = product.pricing?.currency || 'USD';
  const pricing = product.pricing || {};
  const src = (key: keyof ProductDraft, path: string) => fieldSource(product, draft[key], base[key], path);

  // Sale chip: live when today is inside the window.
  const now = Date.now();
  const saleStart = toDate(pricing.salePriceStartAt)?.getTime();
  const saleEnd = toDate(pricing.salePriceEndAt)?.getTime();
  const saleLive = pricing.salePrice != null && (saleStart == null || saleStart <= now) && (saleEnd == null || saleEnd >= now);
  const market = pricing.marketPricing;
  const dealerCount = market?.dealerPrices?.length ?? 0;

  const text = (key: keyof ProductDraft & string, label: string, opts: { hint?: string; barcode?: boolean; readOnly?: boolean } = {}) => (
    <Field
      key={key}
      label={label}
      source={src(key, key)}
      hint={opts.hint}
      display={String(draft[key])}
      editor={
        editing && !opts.readOnly ? (
          opts.barcode ? (
            <BarcodeTextField size="small" fullWidth value={String(draft[key])} onChange={(e) => update({ [key]: e.target.value } as Partial<ProductDraft>)} slotProps={{ htmlInput: { 'aria-label': label } }} />
          ) : (
            <TextInput ariaLabel={label} value={String(draft[key])} onChange={(v) => update({ [key]: v } as Partial<ProductDraft>)} />
          )
        ) : undefined
      }
    />
  );

  const clsRows: Array<{ key: keyof ProductDraft & string; path: string; options?: string[]; groups?: typeof PRODUCT_CATEGORY_SELECT_GROUPS; display: string }> = [
    { key: 'categoryPath', path: 'categoryPath', display: draft.categoryPath.split(/\s*\/\s*/).filter(Boolean).join(' › ') },
    { key: 'category', path: 'category', options: [...PRODUCT_CATEGORY_SLUGS_LIST], groups: PRODUCT_CATEGORY_SELECT_GROUPS, display: draft.category },
    { key: 'categorySlug', path: 'categorySlug', display: draft.categorySlug },
    { key: 'productType', path: 'productType', options: PRODUCT_TYPES, display: draft.productType },
    { key: 'productTypeExpanded', path: 'productTypeExpanded', options: PRODUCT_TYPES_EXPANDED, display: draft.productTypeExpanded },
  ];

  const ynChip = (value: 'Yes' | 'No', yesTone: 'positive' | 'caution') => (
    <Box sx={{ alignSelf: 'flex-start' }}>
      <Pill tone={value === 'Yes' ? yesTone : 'neutral'}>{value}</Pill>
    </Box>
  );

  const stock = product.inventory?.manufacturerStock;
  const ld = product.launchDates;
  const launchPrecision = (value: string, precision?: string): LaunchDatePrecision =>
    precision === 'exact' || precision === 'month' || precision === 'quarter' || precision === 'year'
      ? precision
      : inferLaunchPrecision(value);
  const launchText = (value: string, precision?: string) =>
    value.trim() ? formatLaunchDateValue(value, launchPrecision(value, precision)) : '';
  const launchChip = (
    value: string,
    field: Parameters<typeof fieldProvenanceChip>[0],
    draftKey: 'launchAnnounced' | 'launchUs' | 'launchCanada',
    path: string,
  ) => {
    if (editing) return src(draftKey, path);
    return value.trim() ? fieldProvenanceChip(field, ld?.source, ld?.confidence) : null;
  };

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start' }}>
      {/* Left column */}
      <Box sx={{ flex: '1 1 300px', minWidth: 0, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 1.25, p: '22px', borderRight: `1px solid ${tokens.line.hairline}` }}>
        <Box sx={{ position: 'relative', height: 300, borderRadius: `${tokens.radius.card}px`, bgcolor: tokens.surface.sunken, border: `1px solid ${tokens.line.hairline}`, overflow: 'hidden', display: 'grid', placeItems: 'center' }}>
          {hero ? (
            <Box component="img" src={hero.url} alt={hero.alt || draft.name} sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          ) : (
            <Stack alignItems="center" spacing={1}>
              <CameraIcon sx={{ fontSize: 40, color: tokens.ink.disabled }} />
              <Typography sx={{ fontSize: 12, color: tokens.ink.faint }}>No photos yet</Typography>
            </Stack>
          )}
        </Box>
        {thumbs.length > 1 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: 0.75 }}>
            {thumbs.map((img, i) => {
              const more = i === 5 && extra > 0;
              return (
                <Box
                  key={`${img.url}-${i}`}
                  component="button"
                  type="button"
                  aria-label={more ? `Show all ${images.length} photos` : `Show photo ${i + 1}`}
                  onClick={() => (more ? goTo('media') : setSelected(i))}
                  sx={{ position: 'relative', height: 50, p: 0, display: 'grid', placeItems: 'center', borderRadius: `${tokens.radius.small}px`, bgcolor: tokens.surface.sunken, border: `1px solid ${i === selected && !more ? tokens.accent.main : tokens.line.hairline}`, overflow: 'hidden', cursor: 'pointer' }}
                >
                  <Box component="img" src={img.url} alt="" loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  {more && (
                    <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', bgcolor: alpha(tokens.ink.primary, 0.64), color: tokens.ink.inverse, fontSize: 12, fontWeight: 600 }}>
                      +{extra}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography sx={{ fontSize: 12, color: tokens.ink.faint }}>
            {plural(images.length, 'photo')} · {plural(draft.videos.length, 'video')} · {plural(draft.documentation.length, 'document')}
          </Typography>
          <LinkButton onClick={() => goTo('media')}>Manage media</LinkButton>
        </Stack>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1, pt: 2, borderTop: `1px solid ${tokens.line.hairline}` }}>
          <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: tokens.ink.faint }}>Record</Typography>
          {creating ? (
            <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>Gets its product ID when you submit it.</Typography>
          ) : (
          <KeyValueRows
            mono={false}
            keyWidth="96px"
            rows={
              submitter
                ? [
                    { k: 'Product ID', v: product.id },
                    { k: 'Submitted', v: formatDate(product.createdAt) },
                    { k: 'Updated', v: formatDate(product.updatedAt) },
                  ]
                : [
                    { k: 'Product ID', v: product.id },
                    { k: 'Created', v: formatDate(product.createdAt) },
                    { k: 'Promoted', v: formatDate(product.promotedAt) },
                    { k: 'Updated', v: formatDate(product.updatedAt) },
                    { k: 'Last sync', v: product.source?.lastSyncedAt ? `${formatDateTime(product.source.lastSyncedAt)}${product.source.syncVersion != null ? ` · v${product.source.syncVersion}` : ''}` : '' },
                  ]
            }
          />
          )}
          {!submitter && <LinkButton sx={{ alignSelf: 'flex-start' }} onClick={() => goTo('source')}>Source &amp; history</LinkButton>}
        </Box>
      </Box>

      {/* Right column */}
      <Box sx={{ flex: '999 1 420px', minWidth: 0, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 2, p: '22px', bgcolor: tokens.surface.subtle }}>
        {repairCard}

        {/* Price summary: a submitted product carries no inline price (its prices are the store's own sheet). */}
        {!submitter && (
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2.5, flexWrap: 'wrap', px: '18px', py: 2, border: `1px solid ${tokens.line.default}`, borderRadius: `${tokens.radius.card}px`, bgcolor: tokens.surface.card }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: tokens.ink.faint }}>Retail</Typography>
            <Stack direction="row" alignItems="baseline" spacing={1}>
              <Typography sx={{ fontSize: 28, fontWeight: 700, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>{money(pricing.retail, currency)}</Typography>
              <Typography sx={{ fontSize: 14, color: tokens.ink.faint }}>{currency}</Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.25, flexWrap: 'wrap', rowGap: 0.5 }}>
              {pricing.salePrice != null && (
                <Pill tone="info" dot={false}>
                  {saleLive
                    ? `Sale ${money(pricing.salePrice, currency)}${pricing.salePriceEndAt ? ` until ${formatShortDate(pricing.salePriceEndAt)}` : ''}`
                    : `Sale ${money(pricing.salePrice, currency)} · not live`}
                </Pill>
              )}
              <Box
                component="button"
                type="button"
                onClick={() => goTo('pricing')}
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, border: 'none', background: 'none', p: 0, font: 'inherit', fontSize: 11.5, cursor: 'pointer', color: priceCheck ? tokens.signal.problem.ink : tokens.ink.muted }}
              >
                <Dot tone={priceCheck ? 'problem' : pricing.verification?.status === 'verified' ? 'positive' : 'neutral'} />
                {priceCheck
                  ? `Price check failing${priceCheck.failCount ? ` · ${priceCheck.failCount} in a row` : ''}`
                  : pricing.verification?.lastVerifiedAt
                    ? `Price verified ${formatDate(pricing.verification.lastVerifiedAt)}`
                    : pricing.priceLastChecked
                      ? `Price checked ${formatDate(pricing.priceLastChecked)}`
                      : 'Price not checked yet'}
              </Box>
            </Stack>
          </Box>
          <StatStrip
            items={[
              { label: 'MSRP', value: money(pricing.msrp, currency), note: pricing.manufacturerPriceAsOf ? `since ${formatDate(pricing.manufacturerPriceAsOf)}` : ' ' },
              { label: 'MAP', value: money(pricing.map, currency), note: pricing.mapEndAt ? `until ${formatDate(pricing.mapEndAt)}` : ' ' },
              { label: 'Street avg', value: money(market?.streetPriceAvg, currency), note: dealerCount ? `${dealerCount} dealers` : 'not checked' },
            ]}
          />
        </Box>
        )}

        {/* Classification */}
        <DetailCard
          title="Classification"
          note="Five fields that should agree"
          tone={classification ? 'caution' : undefined}
          actions={classification ? <Pill tone="caution">1 disagrees</Pill> : <Pill tone="positive">All agree</Pill>}
        >
          {classification && (
            <Box sx={{ mx: '18px', mb: 1.25 }}>
              <WarningBanner
                action={
                  <BannerAction
                    disabled={fixing || editing}
                    title={editing ? 'Save or discard your edits first' : undefined}
                    onClick={() => applyFix({ [classification.field]: classification.expected }, `${classification.field} set to ${classification.expected}`)}
                  >
                    Set to {classification.expected}
                  </BannerAction>
                }
              >
                {classification.message}
              </WarningBanner>
            </Box>
          )}
          {clsRows.map((row) => {
            const bad = classification?.field === row.key;
            return (
              <Box
                key={row.key}
                sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'minmax(120px, 180px) minmax(0, 1fr) auto' }, gap: 1.75, alignItems: 'center', minHeight: 42, px: '18px', py: 0.75, borderTop: `1px solid ${tokens.line.faint}`, bgcolor: bad ? tokens.signal.caution.row : tokens.surface.card }}
              >
                <Typography component="span" sx={{ fontFamily: tokens.font.mono, fontSize: 12, color: tokens.ink.muted }}>{row.key}</Typography>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, flexWrap: 'wrap', rowGap: 0.5 }}>
                  {editing ? (
                    row.options ? (
                      <SelectInput ariaLabel={row.key} value={String(draft[row.key])} options={row.options} groups={row.groups} onChange={(v) => update({ [row.key]: v } as Partial<ProductDraft>)} />
                    ) : (
                      <TextInput ariaLabel={row.key} value={String(draft[row.key])} onChange={(v) => update({ [row.key]: v } as Partial<ProductDraft>)} sx={{ flex: '1 1 200px' }} />
                    )
                  ) : (
                    <Typography component="span" sx={{ fontSize: 13.5, color: row.display ? tokens.ink.primary : tokens.ink.faint, overflowWrap: 'anywhere' }}>{row.display || '—'}</Typography>
                  )}
                  <StagedSourceBadge source={src(row.key, row.path)} />
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.6}>
                  <Dot tone={bad ? 'caution' : 'positive'} />
                  <Typography component="span" sx={{ fontSize: 12, whiteSpace: 'nowrap', color: bad ? tokens.signal.caution.ink : tokens.signal.positive.ink }}>
                    {bad ? `Expected ${classification!.expected}` : 'Agrees'}
                  </Typography>
                </Stack>
              </Box>
            );
          })}
        </DetailCard>

        {/* Identity */}
        <DetailCard title="Identity" note={editing ? 'Edits are saved as manual and kept through staged and automatic updates' : undefined}>
          <FieldGrid>
            <Field
              label="Brand"
              source={src('brand', 'brand')}
              display={draft.brand}
              hint={editing && submitter ? (creating ? 'Required' : 'Fixed once submitted') : undefined}
              editor={editing && (!submitter || creating) ? <BrandSelect label="Brand" value={draft.brand} onChange={(brand) => update({ brand })} aria-label="Brand" /> : undefined}
            />
            {text('model', 'Model', { hint: creating ? 'Required' : undefined })}
            {text('sku', submitter ? 'Your SKU' : 'SKU', {
              readOnly: submitter && !creating,
              hint: editing && submitter ? (creating ? creating.skuHint : 'Change it in SKU Crosswalk') : undefined,
            })}
            {text('mpn', 'MPN', { hint: !editing && draft.mpn && draft.mpn === draft.sku ? 'Matches SKU' : undefined })}
            {text('upc', 'UPC', { barcode: true })}
            {text('ean', 'EAN', { barcode: true })}
            {text('gtin', 'GTIN', { barcode: true, hint: !editing && draft.gtin && draft.upc && draft.gtin === draft.upc.padStart(14, '0') ? 'UPC padded to 14 digits' : undefined })}
          </FieldGrid>
          {!submitter && (
            <ChipList
              label="Pricing aliases"
              hint="Alternate model strings matched on price sheets"
              items={draft.pricingAliases}
              editing={editing}
              placeholder="Add alias, press Enter"
              onChange={(pricingAliases) => update({ pricingAliases })}
            />
          )}
        </DetailCard>

        {creating && (
          <DetailCard title="Prices" note={creating.priceNote}>
            <FieldGrid>
              {text('msrp', 'MSRP')}
              {text('map', 'MAP')}
            </FieldGrid>
          </DetailCard>
        )}

        <VariantsCard product={product} editing={editing} onOpen={onOpenProduct} />

        {/* Availability & lifecycle */}
        <DetailCard
          title={submitter ? 'Release dates' : 'Availability & lifecycle'}
          note={ld?.source ? `Launch dates via ${ld.source}${ld.confidence != null ? ` · ${Math.round(ld.confidence <= 1 ? ld.confidence * 100 : ld.confidence)}% confident` : ''}` : undefined}
        >
          <FieldGrid>
            {!submitter && (
              <>
              <Field
                label="Status"
                source={src('status', 'status')}
                display={<Box><Pill tone={statusTone(draft.status)}>{draft.status}</Pill></Box>}
                editor={editing ? <SelectInput ariaLabel="Status" value={draft.status} options={STATUS_OPTIONS} onChange={(status) => update({ status })} /> : undefined}
              />
              <Field
                label="In stock"
                source={src('inStock', 'inventory')}
                display={ynChip(draft.inStock, 'positive')}
                editor={editing ? <SelectInput ariaLabel="In stock" value={draft.inStock} options={['Yes', 'No']} onChange={(v) => update({ inStock: v as 'Yes' | 'No' })} /> : undefined}
              />
              <Field
                label="Discontinued"
                source={src('discontinued', 'inventory')}
                display={ynChip(draft.discontinued, 'caution')}
                editor={editing ? <SelectInput ariaLabel="Discontinued" value={draft.discontinued} options={['Yes', 'No']} onChange={(v) => update({ discontinued: v as 'Yes' | 'No' })} /> : undefined}
              />
              <Field
                label="Manufacturer stock"
                display={stock?.label || (stock?.status ? stock.status.replace(/_/g, ' ') : '')}
                hint={stock?.checkedAt ? `checked ${formatDateTime(stock.checkedAt)}` : 'Read from the manufacturer; not editable'}
              />
              </>
            )}
            <Field
              label="Announced"
              source={launchChip(draft.launchAnnounced, ld?.announcedProvenance, 'launchAnnounced', 'launchDates')}
              display={launchText(draft.launchAnnounced)}
              hint={!editing ? ld?.announcedNote : undefined}
              editor={editing ? <TextInput ariaLabel="Announced" value={draft.launchAnnounced} placeholder="YYYY-MM-DD" onChange={(launchAnnounced) => update({ launchAnnounced })} /> : undefined}
            />
            <Field
              label="US release"
              source={launchChip(draft.launchUs, ld?.us?.dateProvenance, 'launchUs', 'launchDates')}
              display={launchText(draft.launchUs, ld?.us?.precision)}
              hint={ld?.us?.precision ? `${ld.us.precision} precision` : undefined}
              editor={editing ? <TextInput ariaLabel="US release" value={draft.launchUs} placeholder="YYYY-MM or YYYY-MM-DD" onChange={(launchUs) => update({ launchUs })} /> : undefined}
            />
            <Field
              label="Canada release"
              source={launchChip(draft.launchCanada, ld?.canada?.dateProvenance, 'launchCanada', 'launchDates')}
              display={launchText(draft.launchCanada, ld?.canada?.precision)}
              hint={ld?.canada?.precision ? `${ld.canada.precision} precision` : undefined}
              editor={editing ? <TextInput ariaLabel="Canada release" value={draft.launchCanada} placeholder="YYYY-MM or YYYY-MM-DD" onChange={(launchCanada) => update({ launchCanada })} /> : undefined}
            />
            {!submitter && (
              <Field
                label="launchDate"
                source={src('launchDate', 'launchDate')}
                display={draft.launchDate}
                hint="legacy single date"
                editor={editing ? <TextInput ariaLabel="launchDate" value={draft.launchDate} onChange={(launchDate) => update({ launchDate })} /> : undefined}
              />
            )}
          </FieldGrid>
          {!submitter && (
          <Box sx={{ px: '18px', pb: 2, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Typography component="span" sx={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: tokens.ink.muted }}>
              Pre-release lifecycle
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: tokens.ink.muted }}>
              Announced and pre-order products stay status Active; the stage only adds the lifecycle chip for stores with the pre-release lifecycle turned on. Save lifecycle here, or with Save changes.
            </Typography>
            <ProductLifecycleEditor lifecycle={product.lifecycle} launchDates={product.launchDates} editMode={editing} onSave={saveLifecycle} onPendingChange={onLifecyclePending} onInvalidChange={onLifecycleInvalid} />
          </Box>
          )}
        </DetailCard>
      </Box>
    </Box>
  );
};

function variantList(product: AdminCanonicalProduct): AdminProductVariant[] {
  const raw = product.variants;
  if (!Array.isArray(raw)) return [];
  const rows: AdminProductVariant[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const row = item as AdminProductVariant;
    if (!row.id && !row.sku) continue;
    rows.push(row);
  }
  return rows;
}

const isCurrentVariant = (row: AdminProductVariant, product: AdminCanonicalProduct) =>
  row.id === product.id || (!!row.sku && row.sku === product.sku);

/** Style / colour / kit siblings stored on the product. Hidden when this product is the only member. */
const VariantsCard: React.FC<{
  product: AdminCanonicalProduct;
  editing: boolean;
  onOpen?: (next: { id: string; sku?: string; name?: string }) => void;
}> = ({ product, editing, onOpen }) => {
  const stored = variantList(product);
  const others = stored.filter((row) => !isCurrentVariant(row, product));
  if (others.length === 0) return null;

  const optionName = stored.find((row) => row.optionName)?.optionName || 'Style';
  const currentInList = stored.some((row) => isCurrentVariant(row, product));
  const rows = currentInList
    ? [...stored].sort((a, b) => Number(isCurrentVariant(b, product)) - Number(isCurrentVariant(a, product)))
    : [
        { id: product.id, sku: product.sku || '', name: product.name || '', optionName, optionValue: '' },
        ...stored,
      ];

  return (
    <DetailCard title="Variants" count={String(rows.length)} note={optionName}>
      {rows.map((row) => {
        const current = isCurrentVariant(row, product);
        const label = row.optionValue || row.name || row.sku;
        return (
          <Box
            key={row.id || row.sku}
            component="button"
            type="button"
            disabled={!current && (editing || !row.id || !onOpen)}
            onClick={() => {
              if (current || editing || !row.id || !onOpen) return;
              onOpen({ id: row.id, sku: row.sku, name: row.name });
            }}
            title={editing && !current ? 'Save or discard your edits first' : undefined}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              width: '100%',
              px: '18px',
              py: '10px',
              border: 'none',
              borderTop: `1px solid ${tokens.line.hairline}`,
              bgcolor: 'transparent',
              textAlign: 'left',
              cursor: current || editing ? 'default' : 'pointer',
              font: 'inherit',
              '&:hover': current || editing ? undefined : { bgcolor: tokens.surface.sunken },
              '&:disabled': { cursor: 'default', opacity: 0.7 },
            }}
          >
            <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, fontWeight: current ? 600 : 500, color: tokens.ink.primary, overflowWrap: 'anywhere' }}>
                {label}
              </Typography>
              {(row.sku || (row.name && row.name !== label)) && (
                <Typography sx={{ fontSize: 12, color: tokens.ink.faint, fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}>
                  {row.sku}{row.name && row.name !== label ? ` · ${row.name}` : ''}
                </Typography>
              )}
            </Box>
            {current && <Pill tone="neutral" dot={false}>This product</Pill>}
          </Box>
        );
      })}
    </DetailCard>
  );
};
